import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/core/services/auth.service';
import { EventService } from 'src/app/core/services/event.service';
import { MenuService } from 'src/app/core/services/menu.service';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { ChatService } from '../services/chat.service';
import { CHAT_STRINGS_PT, ChatStrings } from '../chat.strings';
import { ChatInvite } from '../chat.model';

/** O aviso por cima do balão: a última mensagem que chegou com o painel fechado. */
export interface ChatLauncherPeek {
  chat: string;
  name: string;
  photo?: string;
  text: string;
  /** Primeira mensagem de uma conversa que ainda não estava na lista. */
  isNew: boolean;
}

/** Um aviso de mensagem desaparece sozinho ao fim disto; o de conversa nova fica até ser aberto ou dispensado. */
const PEEK_MESSAGE_MS = 10000;

/**
 * Balão flutuante do chat (canto inferior direito, em todas as páginas do
 * CRM): um botão com o número de conversas por ler e um painel compacto com
 * o chat em modo "widget" (uma coluna: lista ↔ conversa). Esconde-se na
 * própria página `/chat` e para quem não tem acesso ao chat (VIEW_CHAT).
 *
 * O `app-chat` só nasce na primeira abertura e depois fica escondido, não
 * destruído: mantém a conversa aberta e os ouvintes do socket entre cliques.
 * Enquanto está escondido recebe `active = false`: não marca mensagens como
 * lidas (ninguém as vê) e o badge conta-as; ao abrir, lê a conversa aberta.
 *
 * Aviso dentro da aplicação (o CRM não usa push do browser): com o painel
 * fechado, cada mensagem que chega a quem tem sessão aparece num cartão por
 * cima do balão (quem escreveu e o texto; "Nova conversa" quando a conversa
 * ainda não estava na lista). Clicar abre o painel nessa conversa.
 */
@Component({
  selector: 'app-chat-launcher',
  templateUrl: './chat-launcher.component.html',
  styleUrls: ['./chat-launcher.component.scss']
})
export class ChatLauncherComponent implements OnInit, OnDestroy {
  strings: ChatStrings = CHAT_STRINGS_PT;
  open = false;
  /** Já abriu uma vez: o `app-chat` existe e fica só escondido. */
  created = false;
  unread = 0;
  onChatPage = false;
  hasChat = false;
  peek: ChatLauncherPeek | null = null;
  /** Contrato 6.3, v8: o último convite que chegou com o painel fechado (fica até responder). */
  invitePeek: ChatInvite | null = null;
  /** Convites por aceitar: contam no número do balão. */
  invitesCount = 0;
  inviteBusy = false;

  private subscriptions = new Subscription();
  /** Conversas já conhecidas (da última `GET /chat/users`): o que não está aqui é "nova". */
  private knownChats = new Set<string>();
  private knownLoaded = false;
  private meId?: string;
  private peekTimer: any = null;
  private readonly profileBase = `${environment.apiUrl}/${environment.dirProfileImage}/`;

  constructor(
    private router: Router,
    private authService: AuthService,
    private permissionsService: PermissionsService,
    private chatService: ChatService,
    private modalService: NgbModal,
    private menuService: MenuService,
    private eventService: EventService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.hasChat = this.resolveAccess();
    this.onChatPage = this.router.url.indexOf('/chat') === 0;
    this.subscriptions.add(this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
      this.onChatPage = String(e.urlAfterRedirects || e.url || '').indexOf('/chat') === 0;
      if (this.onChatPage) {
        this.open = false;
        this.dismissPeek();
      }
      this.hasChat = this.resolveAccess();
      this.cdr.markForCheck();
    }));
    if (this.hasChat) {
      this.refreshUnread();
      this.subscriptions.add(this.chatService.receiveMessage().pipe(debounceTime(400)).subscribe(() => this.refreshUnread()));
      this.subscriptions.add(this.chatService.viewedMyMessage().pipe(debounceTime(400)).subscribe(() => this.refreshUnread()));
      this.subscriptions.add(this.chatService.capabilities$.subscribe(caps => {
        this.meId = caps && caps.userAppId ? String(caps.userAppId) : undefined;
      }));
      this.subscriptions.add(this.chatService.receiveMessage().subscribe(data => this.onIncoming(data)));
      this.subscriptions.add(this.chatService.receiveMessageFile().subscribe(data => this.onIncoming(data)));
      // Contrato 6.3, v8: convites por aceitar.
      this.refreshInvites();
      this.subscriptions.add(this.chatService.chatInvite().subscribe(data => this.onInvite(data)));
      this.subscriptions.add(this.chatService.chatInviteCancelled().subscribe(data => this.onInviteGone(data)));
      this.subscriptions.add(this.chatService.chatInviteAnswered().subscribe(data => this.onInviteGone(data)));
      this.subscriptions.add(this.chatService.chatParticipantAdded().subscribe(data => {
        this.onInviteGone(data && data.participant ? { chat: data.chat, userAppId: data.participant.id } : null);
      }));
    }
  }

  /** Número do balão: conversas por ler + convites por aceitar. */
  get badge(): number {
    return this.unread + this.invitesCount;
  }

  /* ---- convites por aceitar (contrato 6.3, v8) ---- */

  private onInvite(data: ChatInvite): void {
    if (!data || !data.chat) {
      return;
    }
    this.refreshInvites();
    if (this.visible && !this.open) {
      this.invitePeek = data;
    }
    this.cdr.markForCheck();
  }

  /** O meu convite saiu (respondido noutro sítio, cancelado, ou aceite): o cartão some. */
  private onInviteGone(data: { chat: string; userAppId: string } | null): void {
    if (!data || !data.chat || !this.meId || String(data.userAppId) !== this.meId) {
      return;
    }
    if (this.invitePeek && this.invitePeek.chat === data.chat) {
      this.invitePeek = null;
    }
    this.refreshInvites();
    this.cdr.markForCheck();
  }

  inviteText(i: ChatInvite): string {
    const by = (i.by && i.by.name) || '';
    const client = i.client && i.client.name;
    return client
      ? this.strings.inviteFrom.replace('{by}', by).replace('{client}', client).trim()
      : this.strings.inviteFromNoClient.replace('{by}', by).trim();
  }

  invitePhoto(i: ChatInvite): string | undefined {
    return this.photoOf(i.client && i.client.profile);
  }

  /** Aceitar no cartão: entro e o painel abre já nessa conversa. */
  acceptInvite(): void {
    const invite = this.invitePeek;
    if (!invite || this.inviteBusy) {
      return;
    }
    this.inviteBusy = true;
    this.chatService.answerInvite(invite.chat, true).subscribe(ack => {
      this.inviteBusy = false;
      this.invitePeek = null;
      this.refreshInvites();
      if (ack && ack.ok) {
        const target = { menu: 'chat', chat: invite.chat };
        if (this.created) {
          this.eventService.broadcast('menu-chat', target);
        } else {
          this.menuService.setData(target);
          this.created = true;
        }
        this.open = true;
      }
      this.cdr.markForCheck();
    });
  }

  declineInvite(): void {
    const invite = this.invitePeek;
    if (!invite || this.inviteBusy) {
      return;
    }
    this.inviteBusy = true;
    this.chatService.answerInvite(invite.chat, false).subscribe(() => {
      this.inviteBusy = false;
      this.invitePeek = null;
      this.refreshInvites();
      this.cdr.markForCheck();
    });
  }

  dismissInvite(): void {
    this.invitePeek = null;
    this.cdr.markForCheck();
  }

  private refreshInvites(): void {
    this.subscriptions.add(this.chatService.getInvites().subscribe(list => {
      this.invitesCount = (list || []).length;
      this.cdr.markForCheck();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.clearPeekTimer();
  }

  get visible(): boolean {
    return this.hasChat && !this.onChatPage;
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      this.created = true;
      this.dismissPeek();
      this.invitePeek = null;
    } else {
      this.refreshUnread();
    }
  }

  /* ---- aviso de mensagem nova ---- */

  /** Clique no aviso: abre o painel já na conversa. */
  openPeek(): void {
    const peek = this.peek;
    this.dismissPeek();
    if (!peek) {
      return;
    }
    const target = { menu: 'chat', chat: peek.chat };
    if (this.created) {
      // O `app-chat` do balão já existe: pede-lhe a conversa (ver ChatComponent.openFromMenu).
      this.eventService.broadcast('menu-chat', target);
    } else {
      // Ainda não existe: consome o pedido do MenuService quando a lista carregar.
      this.menuService.setData(target);
      this.created = true;
    }
    this.open = true;
    this.cdr.markForCheck();
  }

  dismissPeek(): void {
    this.clearPeekTimer();
    if (this.peek) {
      this.peek = null;
      this.cdr.markForCheck();
    }
  }

  /** O rato por cima do aviso segura-o; ao sair, o de mensagem volta a contar. */
  holdPeek(): void {
    this.clearPeekTimer();
  }

  releasePeek(): void {
    if (this.peek && !this.peek.isNew) {
      this.armPeekTimer();
    }
  }

  /**
   * Uma mensagem que chegou pelo socket. Só avisa com o balão visível e fechado,
   * nunca pelo que o próprio enviou, nem por linhas de sistema.
   */
  private onIncoming(data: any): void {
    if (!data || !data.chat || !data.userSender || !this.visible || this.open) {
      return;
    }
    const kind: string = data.kind || 'text';
    const senderId = data.userSender.id ? String(data.userSender.id) : '';
    if (kind === 'system' || !senderId || senderId === this.meId) {
      return;
    }
    const chat = String(data.chat);
    const sameChat = !!this.peek && this.peek.chat === chat;
    const isNew = !!data.chatUser || (this.knownLoaded && !this.knownChats.has(chat)) || (sameChat && this.peek!.isNew);
    this.knownChats.add(chat);
    this.peek = {
      chat,
      name: data.userSender.name || this.strings.listTitle,
      photo: this.photoOf(data.userSender.profile),
      text: this.previewOf(data, kind),
      isNew
    };
    if (isNew) {
      this.clearPeekTimer();
    } else {
      this.armPeekTimer();
    }
    this.cdr.markForCheck();
  }

  /** O texto do aviso: a mensagem, ou o nome do cartão / anexo quando não há texto. */
  private previewOf(data: any, kind: string): string {
    const text = typeof data.message === 'string' ? data.message.trim() : '';
    const byKind = this.strings.previewByKind[kind] || '';
    if (kind === 'note') {
      return byKind ? byKind + ': ' + text : text;
    }
    if (text) {
      return text;
    }
    if (byKind) {
      return byKind;
    }
    const n = Array.isArray(data.files) ? data.files.filter((f: any) => f && !f.isDelete).length : Number(data.filesNumber || 0);
    return n > 1 ? this.strings.attachmentsPreview.replace('{n}', String(n)) : this.strings.attachmentPreview;
  }

  /** `userSender.profile` é um nome de ficheiro de `/users/files/`. */
  private photoOf(profile: any): string | undefined {
    if (!profile || typeof profile !== 'string') {
      return undefined;
    }
    return /^(https?:)?\//.test(profile) || profile.indexOf('data:') === 0 ? profile : this.profileBase + profile;
  }

  private armPeekTimer(): void {
    this.clearPeekTimer();
    this.peekTimer = setTimeout(() => {
      this.peekTimer = null;
      this.peek = null;
      this.cdr.markForCheck();
    }, PEEK_MESSAGE_MS);
  }

  private clearPeekTimer(): void {
    if (this.peekTimer) {
      clearTimeout(this.peekTimer);
      this.peekTimer = null;
    }
  }

  close(): void {
    this.open = false;
    this.refreshUnread();
  }

  /** Escape fecha o balão, mas não por cima de um modal do chat (esse é que fecha). */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open && !this.modalService.hasOpenModals()) {
      this.close();
    }
  }

  /** O mesmo critério do AuthService.hasChatAccess: utilizador de entidade com VIEW_CHAT (MASTER faz bypass). */
  private resolveAccess(): boolean {
    const user: any = this.authService.currentUserValue;
    const roleName = user && user.role ? user.role.name : undefined;
    if (!user || !roleName || !this.permissionsService.isNotAdmin(roleName)) {
      return false;
    }
    if (!this.permissionsService.userAuth) {
      this.permissionsService.userAuth = user;
    }
    return this.permissionsService.hasPermission('VIEW_CHAT');
  }

  /** Conversas com mensagens por ler (o mesmo `GET /chat/users` da lista; só `unreadCount` conta). */
  private refreshUnread(): void {
    this.subscriptions.add(this.chatService.getAllUsersChat().subscribe((list: any[]) => {
      const rows = list || [];
      this.unread = rows.filter((u: any) => u && Number(u.unreadCount || 0) > 0).length;
      rows.forEach((u: any) => {
        if (u && u.chat) {
          this.knownChats.add(String(u.chat));
        }
      });
      this.knownLoaded = true;
      this.cdr.markForCheck();
    }));
  }
}
